import React from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Callout, DataListItem, RowsSkeletonLoader } from '@node-space/ui-components'
import { DateFormat, PathNames } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { ContactCase } from 'types/contacts'
import { useContactCaseSidePanelHelper } from '../hooks/useContactCaseSidePanelHelper'
import { ChannelPaymentSidePanelBody } from './ChannelPaymentSidePanelBody'
import { DepositSidePanelBody } from './DepositSidePanelBody'
import { PaymentInSidePanelBody } from './PaymentInSidePanelBody'

export type ContactCaseSidePanelBodyProps = {
  contactCase: ContactCase
}

export const ContactCaseSidePanelBody = ({ contactCase }: ContactCaseSidePanelBodyProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const paymentContextType = contactCase?.paymentContext?.type

  const { isLoading, isError, payment, channel, isChannelPayment, isPaymentIn, isDirectDeposit } =
    useContactCaseSidePanelHelper({
      contactCase,
      paymentContextType,
    })

  const deepLinkToOriginalPayments = (reference: string) => {
    if (isChannelPayment) {
      navigate(`${PathNames.PAYMENTS}${PathNames.CHANNEL}?dlt-reference=${reference}`)
    } else {
      navigate(`${PathNames.PAYMENTS}${PathNames.CRYPTO}?dlt-reference=${reference}`)
    }
  }

  return (
    <>
      {isError && !isLoading && (
        <Callout variant="error">{t('somethingWentWrongFetchTransaction')}</Callout>
      )}

      {isLoading && !isError && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

      {!isLoading && !isError && (
        <Box flexCol gapY={12}>
          <h4 className="text-h4 text-foreground-prominent">{t('transactionDetails')}</h4>
          {isDirectDeposit && <DepositSidePanelBody contactCase={contactCase} />}

          {isPaymentIn && <PaymentInSidePanelBody payment={payment} contactCase={contactCase} />}
          {isChannelPayment && (
            <ChannelPaymentSidePanelBody channel={channel} contactCase={contactCase} />
          )}

          <DataListItem
            id="date"
            testId="date"
            title={t('date')}
            body={dayjs(contactCase?.paymentCreated).format(DateFormat.D_MMM_YYYY_AT_HH_MM)}
          />

          {(payment?.reference || channel?.reference) && (
            <Box flex justify="center" paddingY={12}>
              <Button
                onClick={() => deepLinkToOriginalPayments(payment?.reference || channel?.reference)}
                className="w-full"
                noPadding
              >
                {t('wallets.searchTransaction.originalDeepLinkText')}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </>
  )
}
